import { useEffect, useState } from "react";
import useReveal from "@/hooks/useReveal";
import { PRIMARY_SHOPIFY_URL } from "@/lib/shopify";

function useCountdown(target) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const diff = Math.max(0, new Date(target).getTime() - now.getTime());
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((diff / (1000 * 60)) % 60);
  const seconds = Math.floor((diff / 1000) % 60);
  return { days, hours, minutes, seconds };
}

function Cell({ value, label, testid }) {
  const v = String(value).padStart(2, "0");
  return (
    <div className="text-center" data-testid={testid}>
      <div className="font-display text-white text-[clamp(2.4rem,5.5vw,4.6rem)] leading-none tracking-tight">
        {v}
      </div>
      <div className="mt-3 font-label text-[#8a8a85]">{label}</div>
    </div>
  );
}

export default function Scarcity({ stock }) {
  const r = useReveal();
  const launch = stock?.launch_date || "2026-06-15T00:00:00Z";
  const { days, hours, minutes, seconds } = useCountdown(launch);
  const total = stock?.total_pieces ?? 300;
  const sold = stock?.sold ?? 113;
  const remaining = stock?.remaining ?? total - sold;
  const pct = Math.min(100, Math.max(0, (sold / total) * 100));

  return (
    <section
      data-testid="scarcity-section"
      className="relative bg-[#070707] py-24 md:py-36 border-y border-[#1a1a1a] overflow-hidden"
    >
      {/* Marquee strip */}
      <div className="marquee text-[#1a1a1a] font-display text-[clamp(3rem,8vw,7rem)] absolute -top-2 left-0 right-0 leading-none">
        <div className="marquee-track">
          <span className="px-8">LIMITED · EDITION 001 · ONLY 300 · DESIGNED FOR THE CULTURE ·</span>
          <span className="px-8">LIMITED · EDITION 001 · ONLY 300 · DESIGNED FOR THE CULTURE ·</span>
          <span className="px-8">LIMITED · EDITION 001 · ONLY 300 · DESIGNED FOR THE CULTURE ·</span>
          <span className="px-8">LIMITED · EDITION 001 · ONLY 300 · DESIGNED FOR THE CULTURE ·</span>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 relative z-10">
        <div
          ref={r.ref}
          className={`reveal ${r.inView ? "in-view" : ""} text-center mt-10`}
        >
          <span className="font-label text-[#d4af37]">— The Scarcity —</span>
          <h2 className="mt-5 font-display text-[clamp(2rem,4.6vw,4rem)] leading-[1.04] tracking-tight text-white max-w-3xl mx-auto">
            Only{" "}
            <span className="font-editorial italic gold-text-gradient">
              300 handcrafted pieces
            </span>{" "}
            worldwide.
          </h2>
          <p className="mt-5 text-[#a3a3a3] max-w-xl mx-auto font-light">
            Once preorders close, this edition will never be made again.
          </p>

          {/* Countdown */}
          <div
            className="mt-14 grid grid-cols-4 gap-4 md:gap-12 max-w-3xl mx-auto"
            data-testid="countdown-grid"
          >
            <Cell value={days} label="Days" testid="countdown-days" />
            <Cell value={hours} label="Hours" testid="countdown-hours" />
            <Cell value={minutes} label="Minutes" testid="countdown-minutes" />
            <Cell value={seconds} label="Seconds" testid="countdown-seconds" />
          </div>

          {/* Stock bar */}
          <div className="mt-16 max-w-2xl mx-auto">
            <div className="flex items-center justify-between font-label text-[#9a9a93] mb-3">
              <span data-testid="scarcity-sold">{sold} Reserved</span>
              <span data-testid="scarcity-remaining">{remaining} Remaining</span>
            </div>
            <div className="h-px bg-[#2a2a2a] relative overflow-hidden">
              <div
                className="absolute inset-y-0 left-0 bg-[#d4af37]"
                style={{
                  width: `${pct}%`,
                  boxShadow: "0 0 14px rgba(212,175,55,0.65)",
                }}
              />
            </div>
            <div className="mt-3 text-xs text-[#6a6a65] tracking-[0.22em] uppercase">
              Edition 001 · {total} pieces total
            </div>
          </div>

          <a
            href={PRIMARY_SHOPIFY_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="scarcity-preorder-btn"
            className="btn-gold mt-14 inline-flex"
          >
            Secure Your Piece
            <span aria-hidden>→</span>
          </a>
        </div>
      </div>
    </section>
  );
}
